import type { LANGUAGE_CODE } from '../interface';
import type { IBrowser } from '../interface/browser';
import { IBrowserPlatform } from '../interface/browser';

const USER_AGENTS_IOS = new Set([
    'iPad Simulator',
    'iPhone Simulator',
    'iPod Simulator',
    'iPad',
    'iPhone',
    'iPod',
]);

const DEFAULT_LANGUAGE: LANGUAGE_CODE = 'en';
const ISO_639_1_STRING_LENGTH = 2;

function _navigator(): (Navigator & { userLanguage?: string | undefined }) | undefined {
    return globalThis.navigator;
}

function _document(): Document | undefined {
    return globalThis.document;
}

function _window(): Window | undefined {
    return globalThis.window;
}

function _platform(): IBrowserPlatform {
    const nav = _navigator();

    if (!nav) {
        return IBrowserPlatform.Unknown;
    }

    const userAgent = nav.userAgent || '';

    const isAndroid = /Android/giu.exec(userAgent);

    if (isAndroid) {
        return IBrowserPlatform.MobileAndroid;
    }

    const document = _document();

    const isMac = userAgent.includes('Mac') && document && 'ontouchend' in document;

    if (isMac ?? false) {
        return IBrowserPlatform.MobileiOS;
    }

    const platform = nav.platform || '';
    const isIOS = USER_AGENTS_IOS.has(platform);

    if (isIOS) {
        return IBrowserPlatform.MobileiOS;
    }

    return IBrowserPlatform.Unknown;
}

function _isMobile(): boolean {
    const nav = _navigator();

    if (!nav) {
        return false;
    }

    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/giu.test(nav.userAgent);
}

function _isInstalled(): boolean {
    const nav = _navigator() as Record<string, unknown> | undefined;

    const isStandalone = Boolean(nav?.['standalone']).valueOf();

    if (!isStandalone) {
        return true;
    }

    const document = _document();

    if (document?.referrer.includes('android-app://') ?? false) {
        return true;
    }

    const win = _window();

    if (win?.matchMedia && win.matchMedia('(display-mode: standalone)').matches) {
        return true;
    }

    return false;
}

/* Primitive implementation, return the original value if '-' not found, otherwise take the 1st arg post split e.g. en-US -> en */
function _BCP47ToISO6391(bcp47: string): string {
    if (bcp47.length <= ISO_639_1_STRING_LENGTH) {
        return bcp47;
    }

    if (!bcp47.includes('-')) {
        return bcp47;
    }

    return bcp47.split('-').at(0) ?? bcp47;
}

function _language(): LANGUAGE_CODE {
    const nav = _navigator();

    if (!nav) {
        return DEFAULT_LANGUAGE;
    }

    const languageBCP47 = String(nav.languages[0] ?? nav.userLanguage ?? navigator.language);

    const languageISO6391 = _BCP47ToISO6391(languageBCP47);

    return languageISO6391 as LANGUAGE_CODE;
}

function browser(): IBrowser {
    const returnValue: IBrowser = {
        platform: _platform(),
        isMobile: _isMobile(),
        isInstalled: _isInstalled(),
        language: _language(),
    };
    return returnValue;
}

export default browser;
